
  import { defineComponent } from 'vue';

  import Constants from '@/common/constants';
  import security from '@/common/mixins/security';
  import { TStorePaths } from '@/common/types/storePaths';

  import AggregatedPageWrapper from '../components/common/AggregatedPageWrapper.vue';
  import { TExportConfig } from '../components/common/ved-aggregated-export-buttons/types';
  import RegionAggregatedBaseInfo from '../components/region-aggregated/RegionAggregatedBaseInfo.vue';
  import RegionAggregatedFilter from '../components/region-aggregated/RegionAggregatedFilter.vue';
  import RegionAggregatedTable from '../components/region-aggregated/RegionAggregatedTable.vue';
  import {
    REGION_AGGREGATED_ACTIONS_NAME,
    REGION_AGGREGATED_GETTERS_NAME,
    REGION_AGGREGATED_MUTATIONS_NAME,
  } from '../store/regionAggregated/name';
  import { ProductValues, SelectTypes, TreeCountryValues, TreeRegionRFValues } from '../types/common-aggregated';
  import { TAggregatedPermissions } from '../types/permission';
  import type { TRegionAggregatedFilter } from '../types/region-aggregated';
  import { getFilenameWithPeriod } from '../utils/filename';

  import links from '@/utils/links';

  const DEFAULT_FILTER: TRegionAggregatedFilter = {
    periods: ['2021'],
    countries: [TreeCountryValues.ARMENIA_99],
    regionsRf: [
      TreeRegionRFValues.RF,
      // Центральный федеральный округ
      TreeRegionRFValues.FO1,
      TreeRegionRFValues.BELGOROD,
      TreeRegionRFValues.BRYANSK,
      TreeRegionRFValues.VLADIMIR,
      TreeRegionRFValues.VORONEZH,
      TreeRegionRFValues.IVANOVO,
      TreeRegionRFValues.KALUGA,
      TreeRegionRFValues.KOSTROMA,
      TreeRegionRFValues.KURSK,
      TreeRegionRFValues.LIPETSK,
      TreeRegionRFValues.MOSCOW_REGION,
      TreeRegionRFValues.OREL,
      TreeRegionRFValues.RYAZAN,
      TreeRegionRFValues.SMOLENSK,
      TreeRegionRFValues.TAMBOV,
      TreeRegionRFValues.TVER,
      TreeRegionRFValues.TULA,
      TreeRegionRFValues.YAROSLAVL,
      TreeRegionRFValues.MOSCOW,
      // Северо-Западный федеральный округ
      TreeRegionRFValues.FO2,
      TreeRegionRFValues.KARELIA,
      TreeRegionRFValues.KOMI,
      TreeRegionRFValues.ARKHANGELSK,
      TreeRegionRFValues.VOLOGDA,
      TreeRegionRFValues.KALININGRAD,
      TreeRegionRFValues.LENINGRAD,
      TreeRegionRFValues.MURMANSK,
      TreeRegionRFValues.NOVGOROD,
      TreeRegionRFValues.PSKOV,
      TreeRegionRFValues.SAINT_PETERSBURG,
      TreeRegionRFValues.NENETS,
      // Южный федеральный округ
      TreeRegionRFValues.FO3,
      TreeRegionRFValues.ADYGEA,
      TreeRegionRFValues.KALMYKIA,
      TreeRegionRFValues.KRASNODAR,
      TreeRegionRFValues.ASTRAKHAN,
      TreeRegionRFValues.VOLGOGRAD,
      TreeRegionRFValues.ROSTOV,
      // Северо-Кавказский федеральный округ
      TreeRegionRFValues.FO4,
      TreeRegionRFValues.DAGESTAN,
      TreeRegionRFValues.INGUSHETIA,
      TreeRegionRFValues.KABARDINO_BALKARIA,
      TreeRegionRFValues.KARACHAY_CHERKESSIA,
      TreeRegionRFValues.NORTH_OSSETIA,
      TreeRegionRFValues.CHECHNYA,
      TreeRegionRFValues.STAVROPOL,
      // Приволжский федеральный округ
      TreeRegionRFValues.FO5,
      TreeRegionRFValues.BASHKORTOSTAN,
      TreeRegionRFValues.MARI_EL,
      TreeRegionRFValues.MORDOVIA,
      TreeRegionRFValues.TATARSTAN,
      TreeRegionRFValues.UDMURTIA,
      TreeRegionRFValues.CHUVASHIA,
      TreeRegionRFValues.KIROV,
      TreeRegionRFValues.NIZHNY_NOVGOROD,
      TreeRegionRFValues.ORENBURG,
      TreeRegionRFValues.PENZA,
      TreeRegionRFValues.PERM,
      TreeRegionRFValues.SAMARA,
      TreeRegionRFValues.SARATOV,
      TreeRegionRFValues.ULYANOVSK,
      // Уральский федеральный округ
      TreeRegionRFValues.FO6,
      TreeRegionRFValues.KURGAN,
      TreeRegionRFValues.SVERDLOVSK,
      TreeRegionRFValues.TYUMEN,
      TreeRegionRFValues.CHELYABINSK,
      TreeRegionRFValues.KHANTY_MANSIYSK,
      TreeRegionRFValues.YAMALO_NENETS,
      // Сибирский федеральный округ
      TreeRegionRFValues.FO7,
      TreeRegionRFValues.ALTAI_REPUBLIC,
      TreeRegionRFValues.BURYATIA,
      TreeRegionRFValues.TUVA,
      TreeRegionRFValues.KHAKASSIA,
      TreeRegionRFValues.ALTAI_KRAI,
      TreeRegionRFValues.ZABAYKALSKY,
      TreeRegionRFValues.KRASNOYARSK,
      TreeRegionRFValues.IRKUTSK,
      TreeRegionRFValues.KEMEROVO,
      TreeRegionRFValues.NOVOSIBIRSK,
      TreeRegionRFValues.OMSK,
      TreeRegionRFValues.TOMSK,
      // Дальневосточный федеральный округ
      TreeRegionRFValues.FO8,
      TreeRegionRFValues.SAKHA,
      TreeRegionRFValues.KAMCHATKA,
      TreeRegionRFValues.PRIMORSKY,
      TreeRegionRFValues.KHABAROVSK,
      TreeRegionRFValues.AMUR,
      TreeRegionRFValues.MAGADAN,
      TreeRegionRFValues.SAKHALIN,
      TreeRegionRFValues.JEWISH_AUTONOMOUS,
      TreeRegionRFValues.CHUKOTKA,
      // Регион не указан
      TreeRegionRFValues.REGION_NOT_SPECIFIED,
    ],
    selectTypeId: SelectTypes.PERIOD,
    productCodes: [ProductValues.ALL_PRODUCTS],
  };

  export default defineComponent({
    name: 'RegionAggregated',
    components: {
      AggregatedPageWrapper,
      RegionAggregatedFilter,
      RegionAggregatedBaseInfo,
      RegionAggregatedTable,
    },
    mixins: [security],
    data(): {
      filter: TRegionAggregatedFilter;
      filterForExport: TRegionAggregatedFilter;
      defaultFilter: TRegionAggregatedFilter;
      storePaths: TStorePaths;
      productText: Optional<string>;
    } {
      return {
        filter: { ...DEFAULT_FILTER },
        filterForExport: { ...DEFAULT_FILTER },
        defaultFilter: { ...DEFAULT_FILTER },
        storePaths: {
          action: REGION_AGGREGATED_ACTIONS_NAME.searchRegionAggregated,
          getter: REGION_AGGREGATED_GETTERS_NAME.getRegionAggregated,
          mutation: REGION_AGGREGATED_MUTATIONS_NAME.setRegionAggregated,
        },
        productText: undefined,
      };
    },
    mounted() {
      this.checkPermissionAndRedirect(this.getPermissions.view);
    },
    computed: {
      getPermissions(): TAggregatedPermissions {
        return {
          view: Constants.Permissions.ForeignTradeAnalyticsAggregatedByRegionsView,
          build: Constants.Permissions.ForeignTradeAnalyticsAggregatedByRegionsBuild,
          export: Constants.Permissions.ForeignTradeAnalyticsAggregatedByRegionsDownload,
        };
      },
      getExportConfig(): TExportConfig {
        return {
          pdfFileName: this.getFileName(this.filter),
          excelFileName: this.getFileName(this.filterForExport),
          excel: {
            permissionCode: this.getPermissions.export,
            url: links.ved.regionAggregated.exportExcel,
            params: this.filterForExport,
          },
        };
      },
    },
    methods: {
      searchFilter(filter: Partial<TRegionAggregatedFilter>) {
        this.filter = { ...this.filter, ...filter };
      },
      updateFilter(filter: Partial<TRegionAggregatedFilter>) {
        this.filterForExport = { ...this.filterForExport, ...filter };
      },
      setProductText(newValue: string) {
        this.productText = newValue;
      },
      getFileName(filter: TRegionAggregatedFilter) {
        return getFilenameWithPeriod('Агрегированные_данные_в_разрезе_регионов', filter.periods);
      },
    },
  });
